import { del, post, put } from "@/requests/server";
import RestModule from "../RestModule";
import i18n from "@/i18n";

export default new RestModule(
  "users",
  {
    form: {
      general: {
        email: {
          type: "email",
          rules: {
            email: true,
          },
        },
        name: {
          type: "text",
          rules: {
            required: true,
          },
        },
        last_name: {
          type: "text",
          rules: {
            required: true,
          },
        },
        avatar: {
          type: "image",
          aspect_ratio: "1 / 1",
        },
        description: {
          type: "textarea",
        },
        date_of_birth: {
          type: "date",
          rules: {
            date: true,
            before: "18 years ago",
          },
        },
        address: {
          type: "text",
          rules: {
            required: true,
          },
        },
        postal_code: {
          type: "text",
        },
        phone: {
          type: "text",
          rules: {
            required: true,
          },
        },
        is_smart_phone: {
          type: "checkbox",
          rules: {
            boolean: true,
          },
        },
        other_phone: {
          type: "text",
        },
        accept_conditions: {
          type: "checkbox",
          rules: {
            accepted: true,
          },
        },
        bank_transit_number: {
          type: "text",
        },
        bank_institution_number: {
          type: "text",
        },
        bank_account_number: {
          type: "text",
        },
      },
      borrower: {
        drivers_license_number: {
          type: "text",
        },
        has_not_been_sued_last_ten_years: {
          type: "checkbox",
          rules: {
            boolean: true,
          },
        },
        gaa: {
          type: "files",
        },
        saaq: {
          type: "files",
        },
      },
    },
    empty: {
      name: "",
      borrower: {},
      accept_conditions: false,
    },
  },
  {
    async approveBorrower({ commit, state, getters }, userId) {
      const { data } = await put(`/users/${userId}/borrower/approve`, null, {
        requestOptions: { cancelId: `approve-borrower-${userId}` },
        notifications: {
          action: i18n.t("store.models.users.notifications.borrower.approve.action"),
          onSuccess: i18n.t("store.models.users.notifications.borrower.approve.success"),
        },
      });

      commit("item", { ...state.item, borrower: data });
      commit("initialItem", { ...getters.initialItem, borrower: data });
    },
    async suspendBorrower({ commit, state, getters }, userId) {
      const { data } = await put(`/users/${userId}/borrower/suspend`, null, {
        requestOptions: { cancelId: `suspend-borrower-${userId}` },
        notifications: {
          action: i18n.t("store.models.users.notifications.borrower.suspend.action"),
          onSuccess: i18n.t("store.models.users.notifications.borrower.suspend.success"),
        },
      });

      commit("item", { ...state.item, borrower: data });
      commit("initialItem", { ...getters.initialItem, borrower: data });
    },
    async unsuspendBorrower({ commit, state, getters }, userId) {
      const { data } = await del(`/users/${userId}/borrower/suspend`, {
        requestOptions: { cancelId: `unsuspend-borrower-${userId}` },
        notifications: {
          action: i18n.t("store.models.users.notifications.borrower.unsuspend.action"),
        },
      });

      commit("item", { ...state.item, borrower: data });
      commit("initialItem", { ...getters.initialItem, borrower: data });
    },
    async resendEmailVerification(state, userId) {
      await post(
        `/users/${userId}/resend_email_verification`,
        {},
        {
          requestOptions: { cancelId: "resend-email-verification", expects: [401] },
          notifications: {
            action: i18n.t("store.models.users.notifications.email_verification.action"),
            onSuccess: i18n.t("store.models.users.notifications.email_verification.success"),
          },
        }
      );
    },
    async resetBorrower({ commit, state, getters }, userId) {
      const { data } = await put(`/users/${userId}/borrower/reset`, null, {
        requestOptions: { cancelId: `reset-borrower-${userId}` },
        notifications: {
          action: i18n.t("store.models.users.notifications.borrower.reset.action"),
          onSuccess: i18n.t("store.models.users.notifications.borrower.reset.success"),
        },
      });

      commit("item", { ...state.item, borrower: data });
      commit("initialItem", { ...getters.initialItem, borrower: data });
    },
    async updatePassword({ commit }, { userId, currentPassword, newPassword }) {
      // request to update password
      try {
        await post(
          `/users/${userId}/password`,
          {
            current: currentPassword,
            new: newPassword,
          },
          {
            requestOptions: { cancelId: "update-password", expects: [401] },
            notifications: {
              action: i18n.t("store.models.users.notifications.password.update.action"),
            },
          }
        );
        return true;
      } catch (e) {
        if (e.response?.status === 401) {
          commit(
            "addNotification",
            {
              content: i18n.t("store.models.users.errors.password.content"),
              title: i18n.t("store.models.users.errors.password.title"),
              variant: "danger",
            },
            { root: true }
          );

          return false;
        }
        throw e;
      }
    },
    async update({ commit, state, rootState }, { id, data, params, showToastOnSuccess = true }) {
      commit("loaded", false);
      commit("loading", true);

      const { data: item } = await put(`/${state.slug}/${id}`, data, {
        axiosRequestConfig: { params },
        requestOptions: { cancelId: `update-user-${id}` },
        notifications: {
          action: i18n.t("store.models.users.notifications.update.action"),
          onSuccess: showToastOnSuccess
            ? i18n.t("store.models.users.notifications.update.success")
            : false,
        },
        cleanupCallback: () => commit("loading", false),
      });

      // If the user currently being updated is the logged-in user
      // (rootState.user), then update it's state as well.
      // Only diff with the update method in RestModule.
      if (rootState.user.id === item.id) {
        commit("mergeUser", { ...item }, { root: true });
      }
      commit("item", item);
      commit("initialItem", item);
      commit("loaded", true);
    },
  }
);
